import { PortalApiError, portalApiError } from "./client";

const NETWORK_RETRY_DELAYS = [750, 1_500, 3_000, 5_000, 8_000, 8_000];
const BUSY_RETRY_MS = 2_000;

type ReconnectReason = "connection" | "busy";

function aborted(error: unknown, signal?: AbortSignal) {
	return signal?.aborted || (error instanceof DOMException && error.name === "AbortError");
}

function wait(ms: number, signal?: AbortSignal) {
	return new Promise<void>((resolve, reject) => {
		if (signal?.aborted) {
			reject(new DOMException("Aborted", "AbortError"));
			return;
		}
		const timer = window.setTimeout(resolve, ms);
		signal?.addEventListener(
			"abort",
			() => {
				window.clearTimeout(timer);
				reject(new DOMException("Aborted", "AbortError"));
			},
			{ once: true },
		);
	});
}

function transientStatus(status: number) {
	return status === 502 || status === 503 || status === 504;
}

export async function sendPortalConversation(input: {
	url: string;
	content?: string;
	idempotencyKey?: string;
	signal?: AbortSignal;
	onTextDelta: (text: string) => void;
	onReconnect: (reason: ReconnectReason) => void;
}) {
	let networkAttempt = 0;
	for (;;) {
		try {
			const response = await fetch(input.url, {
				method: "POST",
				credentials: "include",
				signal: input.signal,
				headers: {
					"Content-Type": "application/json",
					...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
				},
				body: JSON.stringify(input.content === undefined ? {} : { content: input.content }),
			});
			if (!response.ok) {
				const error = await portalApiError(response);
				if (error.code === "conversationBusy") {
					input.onReconnect("busy");
					await wait(BUSY_RETRY_MS, input.signal);
					continue;
				}
				if (transientStatus(response.status)) {
					input.onReconnect("connection");
					await wait(
						NETWORK_RETRY_DELAYS[Math.min(networkAttempt++, NETWORK_RETRY_DELAYS.length - 1)],
						input.signal,
					);
					continue;
				}
				throw error;
			}
			if (!response.body) throw new TypeError("Conversation response body was unavailable");
			const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
			let buffer = "";
			let roundRelease: { accepted: boolean; retryAfterMs?: number } | undefined;
			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;
				buffer += value;
				const lines = buffer.split("\n");
				buffer = lines.pop() ?? "";
				for (const line of lines) {
					if (!line) continue;
					const event = JSON.parse(line) as {
						type: string;
						text?: string;
						accepted?: boolean;
						retryAfterMs?: number;
					};
					if (event.type === "textDelta") input.onTextDelta(event.text ?? "");
					if (event.type === "roundRelease" && typeof event.accepted === "boolean")
						roundRelease = {
							accepted: event.accepted,
							...(event.retryAfterMs === undefined ? {} : { retryAfterMs: event.retryAfterMs }),
						};
				}
			}
			return { roundRelease };
		} catch (error) {
			if (aborted(error, input.signal) || error instanceof PortalApiError) throw error;
			input.onReconnect("connection");
			await wait(NETWORK_RETRY_DELAYS[Math.min(networkAttempt++, NETWORK_RETRY_DELAYS.length - 1)], input.signal);
		}
	}
}
